import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success('Recovery email sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send recovery email');
    }
    setLoading(false);
  };

  if (sent) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
        <div className="absolute inset-0 green-mesh opacity-30" />
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative z-10 w-full max-w-md"
        >
          <div className="bg-card border border-border/40 p-8 md:p-10 rounded-sm text-center">
            <CheckCircle className="mx-auto h-16 w-16 text-green-400 mb-6" />
            <h1 className="font-heading text-2xl uppercase tracking-tight mb-4">Check Your Email</h1>
            <p className="text-muted-foreground mb-6">
              We've sent a password recovery link to <span className="text-primary">{email}</span>
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              If you don't see the email, check your spam folder.
            </p>
            <Link to="/login">
              <Button className="bg-primary text-black">
                Back to Login
              </Button>
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
      <div className="absolute inset-0 green-mesh opacity-30" />
      
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 w-full max-w-md"
      >
        <Link to="/login" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary mb-8 transition-colors">
          <ArrowLeft size={20} />
          <span className="text-sm uppercase tracking-widest">Back to Login</span>
        </Link>
        
        <div className="bg-card border border-border/40 p-8 md:p-10 rounded-sm">
          <div className="text-center mb-8">
            <Mail className="mx-auto h-12 w-12 text-primary mb-4" />
            <h1 className="font-heading text-3xl uppercase tracking-tight mb-2">Forgot Password</h1>
            <p className="text-muted-foreground">Enter your email to receive a recovery link</p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="text-sm uppercase tracking-widest text-muted-foreground mb-2 block">Email</label>
              <Input 
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-transparent border-border focus:border-primary rounded-sm"
                placeholder="your@email.com"
                data-testid="forgot-email-input"
              />
            </div>
            
            <Button 
              type="submit"
              disabled={loading}
              className="w-full bg-primary text-black hover:bg-primary/90 uppercase tracking-wider py-6 font-bold"
              data-testid="forgot-submit-btn"
            >
              {loading ? 'Sending...' : 'Send Recovery Link'}
            </Button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default ForgotPasswordPage;
